/**
 * captureMap.ts — Session 28 (hotfix 4)
 *
 * Client-side capture helper for map snapshots. Renders whichever
 * basemap the rep is currently looking at to a JPEG Blob so it can be
 * POSTed to the backend and stored in S3 as a MulchMapSnapshot.
 *
 * Session 28 hotfix 4 (compositor rewrite):
 *   - Root cause of the "Google 2D shows polygons only, no tiles"
 *     regression: Google Maps JS API defaults to VECTOR rendering,
 *     which puts basemap tiles in a WebGL canvas inside the map div.
 *     html2canvas renders WebGL canvases as opaque black, which masks
 *     the tiles behind the (correctly-captured) polygon overlays.
 *   - Fix: for 2D, find every WebGL canvas in the map div, capture
 *     each via captureStream() + grabFrame(), and composite them under
 *     the html2canvas DOM capture (which now uses `ignoreElements` to
 *     skip WebGL canvases so they don't overlay as black).
 *   - Mapbox and Esri tiles register as ImageMapType (using <img>
 *     elements), so they're picked up by the html2canvas pass. Only
 *     the Google-tile WebGL canvas needs the composite treatment.
 *   - For 3D, search for WebGL canvases anywhere under the container
 *     (light DOM + any accessible shadow roots). If the shadow root
 *     is closed (which we suspect for <gmp-map-3d>), we can't reach
 *     the canvas — falls back to html2canvas with a clear log line.
 *
 * Two capture paths:
 *
 *   2D (Google Hybrid, Mapbox Satellite, Esri World Imagery)
 *     — compositor: WebGL layers + html2canvas DOM overlay.
 *
 *   3D (Google Photorealistic 3D — <gmp-map-3d> web component)
 *     — walk all shadow roots, capture the largest WebGL canvas via
 *       captureStream. Falls back to html2canvas on closed shadow DOM.
 */

import type { RefObject } from "react";

export interface CaptureMapOpts {
  is3D: boolean;
  mapDivRef: RefObject<HTMLDivElement | null>;
  map3DContainerRef: RefObject<HTMLDivElement | null>;
  /**
   * Session 28 hotfix 9: optional handle to the google.maps.Map
   * instance. Used to force a redraw of Google's vector renderer
   * (which uses preserveDrawingBuffer:false, so its WebGL buffer is
   * empty at rest) before we grab a frame. Without this the Google 2D
   * capture is polygons-only on a blank background.
   */
  mapInstanceRef?: RefObject<any>;
}

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

const nextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

/**
 * Does this canvas have a WebGL rendering context? Getting a WebGL
 * context on a canvas that was created as 2D returns null, so this
 * is a reliable positive-only check: true means WebGL, false means
 * 2D / not-yet-initialized / OffscreenCanvas.
 */
const isWebGLCanvas = (canvas: HTMLCanvasElement): boolean => {
  try {
    const webgl2 = (canvas.getContext as any)("webgl2");
    if (webgl2) return true;
    const webgl = (canvas.getContext as any)("webgl");
    if (webgl) return true;
    return false;
  } catch {
    return false;
  }
};

/**
 * Walk a subtree (including any accessible shadow roots) collecting
 * every WebGL canvas we can reach. Closed shadow roots are invisible
 * to JS — those canvases can't be captured.
 */
const findAllWebGLCanvases = (root: Element | ShadowRoot): HTMLCanvasElement[] => {
  const results: HTMLCanvasElement[] = [];
  const seen = new WeakSet<Element | ShadowRoot>();

  const walk = (node: Element | ShadowRoot) => {
    if (seen.has(node)) return;
    seen.add(node);

    const canvases = (node as any).querySelectorAll?.("canvas");
    if (canvases) {
      for (const c of Array.from(canvases) as HTMLCanvasElement[]) {
        if (isWebGLCanvas(c)) results.push(c);
      }
    }

    const all = (node as any).querySelectorAll?.("*");
    if (all) {
      for (const el of Array.from(all) as Element[]) {
        const shadow = (el as any).shadowRoot as ShadowRoot | null;
        if (shadow) walk(shadow);
      }
    }
  };

  walk(root);
  return results;
};

/**
 * Cap a canvas at MAX_DIMENSION on the longer edge. Returns the input
 * unchanged if already small enough.
 */
const capCanvasSize = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const longer = Math.max(source.width, source.height);
  if (longer <= MAX_DIMENSION) return source;

  const scale = MAX_DIMENSION / longer;
  const w = Math.round(source.width * scale);
  const h = Math.round(source.height * scale);
  const dest = document.createElement("canvas");
  dest.width = w;
  dest.height = h;
  const ctx = dest.getContext("2d");
  if (!ctx) return source;
  ctx.imageSmoothingEnabled = true;
  (ctx as any).imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, w, h);
  return dest;
};

const canvasToJpegBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });

/**
 * Read a WebGL canvas via captureStream() + ImageCapture.grabFrame().
 * Returns a fresh 2D canvas containing the captured bitmap, or null
 * on any failure.
 *
 * Session 28 hotfix 8: Chrome's ImageCapture.grabFrame() sometimes
 * rejects with `undefined` when the WebGL context has
 * preserveDrawingBuffer:false (which Google's vector renderer does) and
 * hasn't drawn a fresh frame recently. Two fixes here:
 *   1. Defensive error handling \u2014 never trust `err` to be an Error.
 *   2. Retry with a redraw nudge (dispatch a synthetic mousemove on the
 *      map div, which triggers Google's internal render loop) before
 *      giving up.
 */
const captureWebGLCanvas = async (
  canvas: HTMLCanvasElement,
): Promise<HTMLCanvasElement | null> => {
  const nudgeCanvasRedraw = (c: HTMLCanvasElement) => {
    try {
      const rect = c.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      c.dispatchEvent(
        new MouseEvent("mousemove", {
          bubbles: true,
          cancelable: true,
          clientX: cx,
          clientY: cy,
        }),
      );
    } catch {
      /* best-effort */
    }
  };

  const attempt = async (): Promise<HTMLCanvasElement | null> => {
    if (typeof (canvas as any).captureStream !== "function") {
      console.warn("[captureMap] canvas.captureStream is not available");
      return null;
    }
    const stream: MediaStream = (canvas as any).captureStream(0);
    const track = stream.getVideoTracks()[0];
    if (!track) {
      console.warn("[captureMap] captureStream produced no video track");
      return null;
    }

    try {
      await nextFrame();
      await nextFrame();
      if (typeof (track as any).requestFrame === "function") {
        (track as any).requestFrame();
      }

      const ImageCaptureCtor: any = (window as any).ImageCapture;
      if (!ImageCaptureCtor) {
        console.warn(
          "[captureMap] window.ImageCapture is not available (non-Chromium browser?)",
        );
        return null;
      }
      const imageCapture = new ImageCaptureCtor(track);
      const bitmap: ImageBitmap = await imageCapture.grabFrame();

      const dest = document.createElement("canvas");
      dest.width = bitmap.width;
      dest.height = bitmap.height;
      const ctx = dest.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0);
      return dest;
    } finally {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    }
  };

  // First attempt
  try {
    const first = await attempt();
    if (first) return first;
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.message
        : err === undefined || err === null
          ? "grabFrame rejected with no error (empty drawing buffer?)"
          : String(err);
    console.warn("[captureMap] first capture attempt failed: " + msg);
  }

  // Nudge the canvas to trigger a fresh render, wait a few frames, retry.
  nudgeCanvasRedraw(canvas);
  await nextFrame();
  await nextFrame();
  await nextFrame();

  try {
    const second = await attempt();
    if (second) return second;
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.message
        : err === undefined || err === null
          ? "grabFrame rejected with no error (empty drawing buffer?)"
          : String(err);
    console.error(
      "[captureMap] captureStream/grabFrame failed after retry: " + msg,
    );
  }

  return null;
};

/**
 * html2canvas wrapper. Optionally accepts an `ignoreElements` predicate
 * so callers can skip WebGL canvases (which render as opaque black).
 */
const html2canvasCapture = async (
  el: HTMLElement,
  opts: { skipWebGL?: boolean } = {},
): Promise<HTMLCanvasElement | null> => {
  const { default: html2canvas } = await import("html2canvas");
  try {
    return await html2canvas(el, {
      useCORS: true,
      backgroundColor: null,
      logging: false,
      scale: 1,
      ignoreElements: opts.skipWebGL
        ? (node) => {
            if ((node as any).tagName !== "CANVAS") return false;
            return isWebGLCanvas(node as HTMLCanvasElement);
          }
        : undefined,
    });
  } catch (err) {
    console.error("[captureMap] html2canvas failed:", err);
    return null;
  }
};

/**
 * 2D compositor: capture WebGL tile layers via captureStream, DOM/img
 * layers via html2canvas, and composite them at their correct on-screen
 * positions. Handles the Google-vector-tile regression while still
 * covering Mapbox and Esri (which are IMG-based ImageMapTypes).
 */
const captureMap2D = async (
  mapDiv: HTMLDivElement,
  mapInstance?: any,
): Promise<HTMLCanvasElement | null> => {
  const webglCanvases = findAllWebGLCanvases(mapDiv);
  console.log(
    "[captureMap:2D] found " + webglCanvases.length + " WebGL canvas(es) under mapDiv",
  );

  // Session 28 hotfix 9: Google Maps' vector renderer uses
  // preserveDrawingBuffer:false, so its WebGL buffer is empty at rest.
  // Force a redraw via the map API and wait for `idle` before we look
  // at the buffer. Only relevant when we've got a real map instance and
  // at least one WebGL canvas to capture.
  if (mapInstance && webglCanvases.length > 0) {
    try {
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        // Google's own event system: listen once for `idle`, then
        // trigger a no-op pan. panBy(0,0) still schedules a draw pass
        // and fires `idle` when it completes.
        try {
          (window as any).google?.maps?.event?.addListenerOnce(
            mapInstance,
            "idle",
            finish,
          );
        } catch {
          /* fall through to timeout */
        }
        try {
          mapInstance.panBy(0, 0);
        } catch (err) {
          console.warn(
            "[captureMap:2D] mapInstance.panBy failed: " +
              (err instanceof Error ? err.message : String(err)),
          );
          finish();
        }
        // Safety timeout in case `idle` never fires.
        setTimeout(finish, 400);
      });
      // Extra RAFs so the draw actually flushes to the WebGL buffer.
      await nextFrame();
      await nextFrame();
    } catch (err) {
      console.warn(
        "[captureMap:2D] pre-capture redraw failed: " +
          (err instanceof Error ? err.message : String(err)),
      );
    }
  }

  const domCanvas = await html2canvasCapture(mapDiv, { skipWebGL: true });
  if (!domCanvas) return null;

  if (webglCanvases.length === 0) {
    // Fully raster / IMG-based rendering — DOM capture is complete.
    return domCanvas;
  }

  const mapRect = mapDiv.getBoundingClientRect();
  const scale = 1;
  const composite = document.createElement("canvas");
  composite.width = domCanvas.width;
  composite.height = domCanvas.height;
  const ctx = composite.getContext("2d");
  if (!ctx) return domCanvas;

  for (const canvas of webglCanvases) {
    const bitmap = await captureWebGLCanvas(canvas);
    if (!bitmap) {
      console.warn(
        "[captureMap:2D] captureWebGLCanvas returned null for a WebGL layer; that layer will be missing from the composite",
      );
      continue;
    }
    const canvasRect = canvas.getBoundingClientRect();
    const x = (canvasRect.left - mapRect.left) * scale;
    const y = (canvasRect.top - mapRect.top) * scale;
    const w = canvasRect.width * scale;
    const h = canvasRect.height * scale;
    try {
      ctx.drawImage(bitmap, x, y, w, h);
    } catch (err) {
      console.warn(
        "[captureMap:2D] drawImage of WebGL bitmap failed:",
        (err as Error).message,
      );
    }
  }

  // DOM overlay on top — transparent where WebGL layers were.
  ctx.drawImage(domCanvas, 0, 0);
  console.log(
    "[captureMap:2D] composited " + webglCanvases.length + " WebGL layer(s) with DOM overlay",
  );
  return composite;
};

/**
 * Session 28 hotfix 8: 3D capture via getDisplayMedia. Google's
 * Map3DElement uses a closed shadow DOM that we can't reach with
 * captureStream/getContext. But the browser CAN see the pixels \u2014 the
 * screen-share API returns them just fine. Trade-off: one permission
 * prompt per capture.
 *
 * Flow:
 *   1. Request a video-only screen-share, biased to the current tab.
 *   2. Grab a single frame with ImageCapture.grabFrame().
 *   3. Stop the track immediately (no ongoing recording).
 *   4. Crop the frame to the map container's client rect, using the
 *      bitmap-to-viewport scale to translate coordinates.
 *
 * Returns null if the user cancels the picker or the API isn't
 * available (non-Chromium browsers may not support preferCurrentTab).
 */
const captureMap3DViaDisplayMedia = async (
  container: HTMLDivElement,
): Promise<HTMLCanvasElement | null> => {
  if (
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.getDisplayMedia !== "function"
  ) {
    console.warn(
      "[captureMap:3D-dm] navigator.mediaDevices.getDisplayMedia is not available",
    );
    return null;
  }
  const ImageCaptureCtor: any = (window as any).ImageCapture;
  if (!ImageCaptureCtor) {
    console.warn(
      "[captureMap:3D-dm] window.ImageCapture is not available (non-Chromium browser?)",
    );
    return null;
  }

  let stream: MediaStream | null = null;
  try {
    // preferCurrentTab is Chrome-only \u2014 non-Chromium browsers ignore
    // it and show the full picker. That's fine, the user can still
    // pick the current tab manually.
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        // Chromium honors this and defaults the picker to the current
        // tab. Non-Chromium ignores it.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(({ displaySurface: "browser" } as any) as MediaTrackConstraints),
      },
      audio: false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...(({ preferCurrentTab: true, selfBrowserSurface: "include" } as any) as MediaStreamConstraints),
    });
  } catch (err) {
    // User dismissed the picker or an error occurred.
    const msg = err instanceof Error ? err.message : String(err);
    console.warn("[captureMap:3D-dm] getDisplayMedia failed: " + msg);
    return null;
  }

  // Session 28 hotfix 10: track any overlay elements we hide so we
  // can restore them in `finally`. Declared here (outside the try)
  // so it’s in scope for the finally block.
  const hiddenEls: Array<{
    el: HTMLElement;
    prevVis: string;
    prevDisp: string;
  }> = [];

  try {
    const track = stream.getVideoTracks()[0];
    if (!track) {
      console.warn("[captureMap:3D-dm] no video track in display stream");
      return null;
    }

    // Verify the surface is browser-tab. If the user picked window or
    // screen, cropping coords won't line up with the current viewport.
    // We still try in that case but log a warning.
    const settings: any = track.getSettings?.() ?? {};
    if (settings.displaySurface && settings.displaySurface !== "browser") {
      console.warn(
        "[captureMap:3D-dm] user selected " +
          settings.displaySurface +
          ", not current tab \u2014 crop may misalign",
      );
    }

    // Session 28 hotfix 10 (14): efficient ancestor-walk hide. Rather
    // than walking every element in the document (O(N) with a
    // getComputedStyle per element), we walk from the map3D container
    // up to <body> and hide only the siblings at each level. Coverage
    // is the same \u2014 body-level portal-rendered elements (the modal
    // is appended to body) are caught in the final iteration \u2014 but
    // typical work drops from ~500 elements to ~50.
    //
    // Split by position:
    //   - fixed/absolute: `display: none` (safe, out of flow).
    //   - flow (static/relative): `visibility: hidden` (preserves
    //     layout box so the map container stays the same size).
    try {
      let node: Element | null = container;
      while (node && node !== document.body) {
        const parent: Element | null = node.parentElement;
        if (!parent) break;
        for (const sibling of Array.from(parent.children)) {
          if (sibling === node) continue;
          const he = sibling as HTMLElement;
          const cs = window.getComputedStyle(he);
          if (cs.display === "none" || cs.visibility === "hidden") continue;
          const isFloating =
            cs.position === "fixed" || cs.position === "absolute";
          hiddenEls.push({
            el: he,
            prevVis: he.style.visibility,
            prevDisp: he.style.display,
          });
          if (isFloating) {
            he.style.display = "none";
          } else {
            he.style.visibility = "hidden";
          }
        }
        node = parent;
      }
    } catch {
      /* best-effort */
    }

    // Give the browser a beat to hand us a frame after the DOM has
    // repainted post-hide.
    await nextFrame();
    await nextFrame();
    await nextFrame();

    const imageCapture = new ImageCaptureCtor(track);
    let bitmap: ImageBitmap;
    try {
      bitmap = await imageCapture.grabFrame();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[captureMap:3D-dm] grabFrame failed: " + msg);
      return null;
    }

    // Crop to the 3D container. The bitmap covers the ENTIRE shared
    // surface; we want just the container's client rect.
    const rect = container.getBoundingClientRect();
    const scaleX = bitmap.width / window.innerWidth;
    const scaleY = bitmap.height / window.innerHeight;
    const sx = Math.max(0, Math.round(rect.left * scaleX));
    const sy = Math.max(0, Math.round(rect.top * scaleY));
    const sw = Math.min(
      bitmap.width - sx,
      Math.round(rect.width * scaleX),
    );
    const sh = Math.min(
      bitmap.height - sy,
      Math.round(rect.height * scaleY),
    );

    if (sw <= 0 || sh <= 0) {
      console.error(
        "[captureMap:3D-dm] crop dims are non-positive: " +
          sw + "x" + sh + " (bitmap " + bitmap.width + "x" + bitmap.height + ")",
      );
      return null;
    }

    const dest = document.createElement("canvas");
    dest.width = sw;
    dest.height = sh;
    const ctx = dest.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, sw, sh);

    console.log(
      "[captureMap:3D-dm] captured " +
        sw + "x" + sh +
        " from bitmap " + bitmap.width + "x" + bitmap.height,
    );
    return dest;
  } finally {
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    // Restore visibility/display regardless of success or failure.
    try {
      for (const { el, prevVis, prevDisp } of hiddenEls) {
        el.style.visibility = prevVis;
        el.style.display = prevDisp;
      }
    } catch {
      /* best-effort */
    }
  }
};

/**
 * 3D capture: search everywhere for a WebGL canvas (light DOM + all
 * accessible shadow roots). Capture the largest via captureStream.
 * Falls back to html2canvas on the container (which will produce a
 * mostly-black image if the canvas is behind a closed shadow root).
 */
const captureMap3D = async (
  container: HTMLDivElement,
): Promise<HTMLCanvasElement | null> => {
  // First try the accessible WebGL canvas path in case a future SDK
  // version opens the shadow DOM. Cheap check, no user prompt.
  const webglCanvases = findAllWebGLCanvases(container);
  console.log(
    "[captureMap:3D] found " +
      webglCanvases.length +
      " WebGL canvas(es) under container (searched light DOM + open shadow roots)",
  );

  if (webglCanvases.length > 0) {
    webglCanvases.sort((a, b) => b.width * b.height - a.width * a.height);
    for (const canvas of webglCanvases) {
      const bitmap = await captureWebGLCanvas(canvas);
      if (bitmap) {
        console.log(
          "[captureMap:3D] captured WebGL canvas " +
            bitmap.width +
            "x" +
            bitmap.height,
        );
        return bitmap;
      }
    }
    console.warn(
      "[captureMap:3D] all captureStream attempts failed; falling through to getDisplayMedia",
    );
  } else {
    const map3D = container.querySelector("gmp-map-3d") as any;
    if (map3D) {
      const shadowStatus =
        map3D.shadowRoot === null
          ? "null (closed shadow DOM \u2014 unreachable from JS)"
          : "accessible but no canvas found inside";
      console.log(
        "[captureMap:3D] <gmp-map-3d> shadowRoot is " +
          shadowStatus +
          ". Using getDisplayMedia (tab-share) path.",
      );
    } else {
      console.log(
        "[captureMap:3D] no <gmp-map-3d> element in container. Trying getDisplayMedia anyway.",
      );
    }
  }

  // Session 28 hotfix 8: fallback via browser screen-share. Prompts
  // the user to share this tab; we then crop to the map container.
  const viaDisplayMedia = await captureMap3DViaDisplayMedia(container);
  if (viaDisplayMedia) return viaDisplayMedia;

  console.warn(
    "[captureMap:3D] getDisplayMedia failed or was cancelled; falling back to html2canvas (will likely be black)",
  );
  return html2canvasCapture(container);
};

export const captureMapAsBlob = async (
  opts: CaptureMapOpts,
): Promise<Blob | null> => {
  let source: HTMLCanvasElement | null = null;

  if (opts.is3D) {
    const container = opts.map3DContainerRef.current;
    if (!container) {
      console.error(
        "[captureMap:3D] map3DContainerRef.current is null. Container not mounted?",
      );
      return null;
    }
    source = await captureMap3D(container);
  } else {
    const mapDiv = opts.mapDivRef.current;
    if (!mapDiv) {
      console.error("[captureMap:2D] mapDivRef.current is null");
      return null;
    }
    source = await captureMap2D(mapDiv, opts.mapInstanceRef?.current);
  }

  if (!source) return null;

  const capped = capCanvasSize(source);
  const blob = await canvasToJpegBlob(capped);
  if (!blob) {
    console.error("[captureMap] canvas.toBlob returned null");
    return null;
  }
  console.log(
    "[captureMap] final capture " +
      capped.width +
      "x" +
      capped.height +
      " -> " +
      Math.round(blob.size / 1024) +
      " KB (JPEG q=" +
      JPEG_QUALITY +
      ")",
  );
  return blob;
};
